import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CuentasManager } from "@/components/entities/CuentasBancarias";
import { PersonaPicker } from "@/components/entities/PersonaPicker";

const soloFisicas = (p) => p.tipo === "fisica";

function PersonaLigada({ label, name, value, onChange, personas, selfId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <PersonaPicker value={value} onChange={(v) => onChange(name, v)} testid={`persona-${name}`}
        personas={personas.filter((p) => p.id !== selfId)} filtro={soloFisicas} />
      <p className="text-[11px] text-slate-500 mt-1">Se elige del catálogo de Personas; si no existe, regístrala primero como persona física.</p>
    </div>
  );
}

function PersonaCuentas({ personaId, titular }) {
  const [cuentas, setCuentas] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get(`/personas/${personaId}/cuentas`);
    setCuentas(data);
  }, [personaId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="pt-3 border-t border-[#E2DCD2]" data-testid="persona-cuentas">
      <div className="text-sm font-semibold text-slate-800 mb-2">Cuentas bancarias</div>
      <CuentasManager personaId={personaId} cuentas={cuentas} defaultTitular={titular} onChanged={load} />
    </div>
  );
}

const empty = {
  tipo: "fisica", nombre: "", nacionalidad: "mexicana",
  // Documento de identidad (independiente de nacionalidad)
  tipo_documento_identidad: "ine", numero_documento_identidad: "",
  // Fiscales (siempre opcionales, aparte del documento)
  curp: "", rfc: "",
  // Legacy — mantenidos para compatibilidad; los nuevos formularios no los editan.
  ine_clave_elector: "", pasaporte: "", documento_migratorio: "", tax_id_extranjero: "",
  estado_civil: "", regimen_patrimonial: "", genero: "masculino",
  // Cónyuge (visible solo si estado_civil === casado_sociedad_conyugal)
  conyuge_nombre: "", conyuge_genero: "",
  conyuge_tipo_documento_identidad: "ine",
  conyuge_numero_documento_identidad: "",
  conyuge_curp: "", conyuge_rfc: "",
  domicilio: "", correo: "", whatsapp: "",
  razon_social: "", rfc_moral: "", representante_legal_nombre: "", instrumento_constitutivo: "",
  representante_persona_id: "", apoderado_persona_id: "",
  actua_por_apoderado: false, apoderado_nombre: "", apoderado_instrumento: "",
};

// Nombre visible del tipo de documento y label del número asociado.
const DOC_TIPOS = [
  { value: "ine", label: "INE / Clave de Elector", inputLabel: "Clave de Elector (INE)" },
  { value: "pasaporte", label: "Pasaporte", inputLabel: "Número de pasaporte" },
  { value: "documento_migratorio", label: "Documento migratorio (FM2 / FM3 / Residente)", inputLabel: "Número de documento migratorio" },
];

const labelForDocNumero = (tipo) =>
  DOC_TIPOS.find((d) => d.value === tipo)?.inputLabel || "Número de documento";

// Field definido a NIVEL DE MÓDULO para evitar remount y pérdida de foco.
const PersonaRevealCtx = createContext(null);

function Field({ label, name, type = "text", placeholder, options, value, onChange, testIdPrefix = "persona" }) {
  const reveal = useContext(PersonaRevealCtx);
  const masked = reveal && String(value || "").includes("****");
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      {options ? (
        <Select value={value || ""} onValueChange={(v) => onChange(name, v)}>
          <SelectTrigger data-testid={`${testIdPrefix}-${name}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div className="relative">
        <input type={type} value={value || ""} onChange={(e) => onChange(name, e.target.value)} data-testid={`${testIdPrefix}-${name}`}
               placeholder={placeholder} onFocus={(e) => { if (masked) e.target.select(); }}
               className={`w-full h-11 px-3 ${masked ? "pr-10" : ""} rounded-lg border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]`} />
        {masked && (
          <button type="button" onClick={() => reveal(name, value)} data-testid={`${testIdPrefix}-reveal-${name}`} aria-label="Ver un momento"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-500"><Eye className="w-4 h-4" /></button>
        )}
        </div>
      )}
    </div>
  );
}

export default function PersonaFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  useEffect(() => {
    if (open && f.tipo === "moral" && !catalogo.length) {
      api.get("/personas").then((r) => setCatalogo(r.data)).catch(() => {});
    }
  }, [open, f.tipo, catalogo.length]);
  useEffect(() => {
    if (open) {
      // Al abrir en modo edición, si la persona sólo tiene datos legacy,
      // pre-cargamos el nuevo campo con lo que exista.
      if (initial) {
        const merged = { ...empty, ...initial };
        if (!merged.tipo_documento_identidad || !merged.numero_documento_identidad) {
          if (initial.ine_clave_elector) {
            merged.tipo_documento_identidad = "ine";
            merged.numero_documento_identidad = initial.ine_clave_elector;
          } else if (initial.pasaporte) {
            merged.tipo_documento_identidad = "pasaporte";
            merged.numero_documento_identidad = initial.pasaporte;
          } else if (initial.documento_migratorio) {
            merged.tipo_documento_identidad = "documento_migratorio";
            merged.numero_documento_identidad = initial.documento_migratorio;
          } else {
            merged.tipo_documento_identidad = merged.tipo_documento_identidad || "ine";
          }
        }
        setF(merged);
      } else {
        setF(empty);
      }
    }
  }, [open, initial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reveal = initial?.id ? async (name, masked) => {
    try {
      const { data } = await api.get(`/personas/${initial.id}/revelar/${name}`);
      if (!data.valor) return;
      set(name, data.valor);
      setTimeout(() => setF((p) => (p[name] === data.valor ? { ...p, [name]: masked } : p)), 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo mostrar"); }
  } : null;

  const save = async () => {
    if (!f.nombre.trim() && !f.razon_social?.trim()) {
      return toast.error("Ingresa el nombre o razón social");
    }
    setSaving(true);
    try {
      const body = { ...f };
      if (f.tipo === "moral" && !body.nombre) body.nombre = body.razon_social;
      if (f.estado_civil !== "casado_sociedad_conyugal") {
        Object.assign(body, { conyuge_nombre: null, conyuge_genero: null, conyuge_numero_documento_identidad: null, conyuge_curp: null, conyuge_rfc: null });
      }
      body.representante_persona_id = body.representante_persona_id || null;
      body.apoderado_persona_id = body.apoderado_persona_id || null;
      body.actua_por_apoderado = !!body.apoderado_persona_id;
      body.conyuge_genero = body.conyuge_genero || null;
      // Sincroniza legacy con nuevo campo para que los PDFs viejos sigan funcionando.
      if (f.tipo === "fisica") {
        if (f.tipo_documento_identidad === "ine") body.ine_clave_elector = f.numero_documento_identidad || "";
        if (f.tipo_documento_identidad === "pasaporte") body.pasaporte = f.numero_documento_identidad || "";
        if (f.tipo_documento_identidad === "documento_migratorio") body.documento_migratorio = f.numero_documento_identidad || "";
      }
      const url = initial?.id ? `/personas/${initial.id}` : "/personas";
      const method = initial?.id ? "put" : "post";
      const { data } = await api[method](url, body);
      toast.success(initial?.id ? "Persona actualizada" : "Persona creada");
      if (data.posibles_duplicados?.length) {
        toast.warning(`Ya existe otra persona con el mismo RFC o CURP: ${data.posibles_duplicados.map((d) => d.nombre).join(", ")}`);
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const showConyuge = f.tipo === "fisica" && f.estado_civil === "casado_sociedad_conyugal";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="persona-form-dialog">
        <PersonaRevealCtx.Provider value={reveal}>
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Editar persona" : "Nueva persona"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo de persona" name="tipo" value={f.tipo} onChange={set} options={[
              { value: "fisica", label: "Persona física" }, { value: "moral", label: "Persona moral (empresa)" },
            ]} />
            {f.tipo === "fisica" && (
              <Field label="Nacionalidad" name="nacionalidad" value={f.nacionalidad} onChange={set} options={[
                { value: "mexicana", label: "Mexicana" }, { value: "extranjera", label: "Extranjera" },
              ]} />
            )}
          </div>

          {f.tipo === "fisica" ? (
            <>
              <Field label="Nombre completo" name="nombre" value={f.nombre} onChange={set} placeholder="Ej. María López Hernández" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Género (para concordancia gramatical)" name="genero" value={f.genero} onChange={set} options={[
                  { value: "masculino", label: "Masculino" }, { value: "femenino", label: "Femenino" },
                ]} />
                <Field label="Estado civil" name="estado_civil" value={f.estado_civil} onChange={set} options={[
                  { value: "soltero", label: f.genero === "femenino" ? "Soltera" : "Soltero" },
                  { value: "casado_separacion", label: `${f.genero === "femenino" ? "Casada" : "Casado"} bajo el régimen de separación de bienes` },
                  { value: "casado_sociedad_conyugal", label: `${f.genero === "femenino" ? "Casada" : "Casado"} bajo el régimen de sociedad conyugal` },
                ]} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Tipo de documento de identidad"
                  name="tipo_documento_identidad"
                  value={f.tipo_documento_identidad}
                  onChange={set}
                  options={DOC_TIPOS}
                />
                <Field
                  label={labelForDocNumero(f.tipo_documento_identidad)}
                  name="numero_documento_identidad"
                  value={f.numero_documento_identidad}
                  onChange={set}
                  placeholder=""
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CURP (opcional)" name="curp" value={f.curp} onChange={set} />
                <Field label="RFC (opcional)" name="rfc" value={f.rfc} onChange={set} />
              </div>
              {f.nacionalidad === "extranjera" && (
                <Field label="Tax ID / RFC extranjero (opcional)" name="tax_id_extranjero" value={f.tax_id_extranjero} onChange={set} />
              )}

              {showConyuge && (
                <div className="rounded-2xl border-2 border-dashed border-[#B8873D]/40 bg-[#FFFBEB]/60 p-4 space-y-3" data-testid="conyuge-block">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#B8873D] text-white flex items-center justify-center text-xs font-bold">C</div>
                    <div>
                      <div className="font-heading font-bold text-slate-900">Datos del cónyuge</div>
                      <div className="text-xs text-slate-600">Comparece a la firma por estar en sociedad conyugal.</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Nombre completo del cónyuge" name="conyuge_nombre" value={f.conyuge_nombre} onChange={set} placeholder="Ej. Juan Pérez García" />
                    <Field label="Género del cónyuge" name="conyuge_genero" value={f.conyuge_genero} onChange={set} options={[
                      { value: "masculino", label: "Masculino" }, { value: "femenino", label: "Femenino" },
                    ]} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Tipo de documento de identidad"
                      name="conyuge_tipo_documento_identidad"
                      value={f.conyuge_tipo_documento_identidad}
                      onChange={set}
                      options={DOC_TIPOS}
                    />
                    <Field
                      label={labelForDocNumero(f.conyuge_tipo_documento_identidad)}
                      name="conyuge_numero_documento_identidad"
                      value={f.conyuge_numero_documento_identidad}
                      onChange={set}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="CURP del cónyuge (opcional)" name="conyuge_curp" value={f.conyuge_curp} onChange={set} />
                    <Field label="RFC del cónyuge (opcional)" name="conyuge_rfc" value={f.conyuge_rfc} onChange={set} />
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="Razón social" name="razon_social" value={f.razon_social} onChange={set} placeholder="Ej. Inmobiliaria López S.A. de C.V." />
              <Field label="RFC" name="rfc_moral" value={f.rfc_moral} onChange={set} />
              <PersonaLigada label="Representante legal" name="representante_persona_id" value={f.representante_persona_id}
                onChange={set} personas={catalogo} selfId={initial?.id} />
              <Field label="Instrumento constitutivo" name="instrumento_constitutivo" value={f.instrumento_constitutivo} onChange={set} placeholder="Escritura pública No..." />
              <PersonaLigada label="Apoderado (opcional)" name="apoderado_persona_id" value={f.apoderado_persona_id}
                onChange={set} personas={catalogo} selfId={initial?.id} />
              {f.apoderado_persona_id && (
                <Field label="Instrumento del poder del apoderado" name="apoderado_instrumento" value={f.apoderado_instrumento} onChange={set} placeholder="Escritura pública No..." />
              )}
            </>
          )}

          <Field label="Domicilio" name="domicilio" value={f.domicilio} onChange={set} placeholder="Calle, número, colonia, ciudad, estado, CP" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Correo electrónico" name="correo" value={f.correo} onChange={set} type="email" />
            <Field label="WhatsApp / Teléfono" name="whatsapp" value={f.whatsapp} onChange={set} />
          </div>
          {initial?.id && <PersonaCuentas personaId={initial.id} titular={f.nombre || f.razon_social} />}
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="text-slate-600 px-4 py-2" data-testid="persona-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="bm-btn-primary" data-testid="persona-save">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </DialogFooter>
        </PersonaRevealCtx.Provider>
      </DialogContent>
    </Dialog>
  );
}
